import { useApiFormErrors } from "@/hooks/useApiFormErrors";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button } from "@hipotecar/ui/button";
import { Input } from "@hipotecar/ui/input";
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import type { ApiValidationError } from "../../schemas";

// Personal information form schema
const personalInfoSchema = z.object({
	primer_nombre: z.string().min(1, "El primer nombre es requerido"),
	apellido: z.string().optional(),
	telefono: z.string().optional(),
});

type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;

interface PersonalInfoFormProps {
	initialData?: Partial<PersonalInfoFormData>;
	onSubmit: (data: PersonalInfoFormData) => Promise<void> | void;
	onCancel: () => void;
	isLoading?: boolean;
	apiErrors?: ApiValidationError[];
}

export default function PersonalInfoForm({
	initialData,
	onSubmit,
	onCancel,
	isLoading = false,
	apiErrors,
}: PersonalInfoFormProps) {
	const { t } = useTranslation(["common", "profile"]);

	const form = useForm<PersonalInfoFormData>({
		resolver: zodResolver(personalInfoSchema),
		defaultValues: initialData || {
			primer_nombre: "",
			apellido: "",
			telefono: "",
		},
	});

	useApiFormErrors(form, apiErrors);

	const onFormSubmit = async (data: PersonalInfoFormData) => {
		await onSubmit(data);
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onFormSubmit)}
				className="flex flex-col"
			>
				<div className="custom-scrollbar max-h-[600px] space-y-4 overflow-y-auto px-2 pb-3">
					{/* First Name and Last Name - Same Row */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="primer_nombre"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("profile:fields.firstName")} *</FormLabel>
									<FormControl>
										<Input {...field} disabled={isLoading} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="apellido"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("profile:fields.lastName")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={field.value ?? ""}
											disabled={isLoading}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{/* Phone */}
					<FormField
						control={form.control}
						name="telefono"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("profile:fields.phone")}</FormLabel>
								<FormControl>
									<Input
										type="tel"
										placeholder="+51 999 999 999"
										{...field}
										value={field.value ?? ""}
										disabled={isLoading}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{/* Form Actions */}
				<div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
					<Button
						type="button"
						size="sm"
						variant="outline"
						onClick={onCancel}
						disabled={isLoading}
					>
						{t("profile:close")}
					</Button>
					<Button type="submit" size="sm" isLoading={isLoading}>
						{t("profile:saveChanges")}
					</Button>
				</div>
			</form>
		</Form>
	);
}
